import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Allows us to listen for breakpoint changes in Javascript land
 * @param queries A list of breakpoints that we are listening for
 * @param values A value that maps to each corresponding breakpoint
 * @param defaultValue The value that is returned if there are no breakpoints that match
 */
export default function useMedia<T>(queries: string[], values: T[], defaultValue: T): T {
  // Array containing a media query list for each query
  const mediaQueryLists = useMemo(() => {
    return typeof window !== 'undefined' ? queries.map((q) => window.matchMedia(q)) : [];
  }, [queries]);

  // Function that gets value based on matching media query
  const getValue = useCallback(() => {
    // Get index of first media query that matches
    const index = mediaQueryLists.findIndex((mql) => mql.matches);
    // Return related value or defaultValue if none
    return typeof values[index] !== 'undefined' ? values[index] : defaultValue;
  }, [mediaQueryLists, values, defaultValue]);

  const [value, setValue] = useState(getValue);

  useEffect(
    () => {
      const handler = () => setValue(getValue);

      mediaQueryLists.forEach((mql) => mql.addListener(handler));
      return () => mediaQueryLists.forEach((mql) => mql.removeListener(handler));
    },
    [getValue, mediaQueryLists], // Empty array ensures effect is only run on mount and unmount
  );

  return value;
}
